import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Network,
  RefreshCcw,
  AlertTriangle,
  FileSpreadsheet,
  PlusCircle,
} from "lucide-react";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common";
import ConfirmDialog from "@/features/user-management/components/ConfirmDialog";
import { useOrganizationStore } from "../hooks";
import OrganizationUnitDialog from "../components/OrganizationUnitDialog";
import { useAuthStore } from "@/features/auth/hooks";
import { Can } from "@/hooks/useCan/Can";
import OrganizationFiltersSection from "../components/OrganizationFiltersSection";
import OrganizationTableSection from "../components/OrganizationTableSection";
import OrganizationExcelDialog from "../components/OrganizationExcelDialog";
import { companyStore } from "@/features/company/stores/company.store";

const resolveGroupBy = (mode) => {
  if (mode === "businessType") return "businessType";
  if (mode === "country") return "country";
  return undefined;
};

const AdminOrganizationPage = observer(() => {
  const { t } = useTran();
  const organizationStore = useOrganizationStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [dialogInitial, setDialogInitial] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const [groupMode, setGroupMode] = useState("level");
  const [excelOpen, setExcelOpen] = useState(false);

  useEffect(() => {
    organizationStore.fetchLookups().catch(() => {});
  }, [organizationStore]);

  useEffect(() => {
    void companyStore.fetchPublicCompanies();
  }, []);

  useEffect(() => {
    const groupBy = resolveGroupBy(groupMode);
    organizationStore.fetchHierarchyAdminAll({ groupBy }).catch(() => {});
  }, [organizationStore, groupMode]);

  const modalCompanyOptions = useMemo(
    () =>
      (companyStore.companyOptions || []).map((option) => ({
        ...option,
        value: String(option.value),
      })),
    [companyStore.companyOptions],
  );

  const handleCreate = (typeHint) => {
    setDialogMode("create");
    setDialogInitial({
      unitLevel: typeHint || "level_1",
      parentId: "",
      region: "",
    });
    setDialogOpen(true);
  };

  const handleEdit = (unit) => {
    if (!unit) return;
    setDialogMode("edit");
    setDialogInitial({
      ...unit,
      parentId: unit.parentId || "",
      region: unit.region || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = (unit) => {
    setPendingDelete(unit);
    setConfirmOpen(true);
  };

  const handleDialogSubmit = async (payload) => {
    const groupBy = resolveGroupBy(groupMode);
    if (dialogMode === "create") {
      await organizationStore.createUnit(payload, { groupBy, adminAll: true });
    } else {
      await organizationStore.updateUnit(dialogInitial.id, payload, {
        groupBy,
        adminAll: true,
      });
    }
    setDialogOpen(false);
  };

  const confirmDeleteUnit = async () => {
    if (!pendingDelete) return;
    const groupBy = resolveGroupBy(groupMode);
    await organizationStore.deleteUnit(pendingDelete.id, {
      groupBy,
      adminAll: true,
    });
    setConfirmOpen(false);
    setPendingDelete(null);
  };

  const handleRefresh = () => {
    const groupBy = resolveGroupBy(groupMode);
    organizationStore.fetchHierarchyAdminAll({ groupBy }).catch(() => {});
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Organization Structure")}
        description={t(
          "Model your parent company and multi-level organization structure.",
        )}
        icon={Network}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Can
              resource="organization"
              action="view"
              userPermissions={permissions}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={organizationStore.loading}
              >
                <RefreshCcw
                  className={`${organizationStore.loading ? "animate-spin" : ""}`}
                />
                {t("Refresh")}
              </Button>
            </Can>

            <Can
              resource="organization"
              action="create"
              userPermissions={permissions}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExcelOpen(true)}
              >
                <FileSpreadsheet className="h-4 w-4" />
                {t("Import / Download")}
              </Button>

              <Button size="sm" type="button" onClick={() => handleCreate()}>
                <PlusCircle />
                {t("Add Unit")}
              </Button>
            </Can>
          </div>
        }
      />

      <OrganizationFiltersSection />
      <OrganizationTableSection onEdit={handleEdit} onDelete={handleDelete} />

      {organizationStore.error && !dialogOpen ? (
        <div className="flex items-center gap-3 rounded-radius border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle size={18} />
          <span>{organizationStore.error}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto text-xs"
            onClick={() => organizationStore.resetError()}
          >
            {t("Dismiss")}
          </Button>
        </div>
      ) : null}
      {organizationStore.lookupError ? (
        <div className="flex items-center gap-3 rounded-radius border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          <span>{organizationStore.lookupError}</span>
        </div>
      ) : null}
      <OrganizationUnitDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={dialogInitial}
        onSubmit={handleDialogSubmit}
        onOpenChange={setDialogOpen}
        adminMode
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("Delete organization unit")}
        description={t(
          "This action is permanent. Child units must be reassigned or removed before deleting.",
        )}
        tone="danger"
        confirmLabel={
          organizationStore.deletingId === pendingDelete?.id
            ? t("Deleting...")
            : t("Delete")
        }
        cancelLabel={t("Cancel")}
        loading={organizationStore.deletingId === pendingDelete?.id}
        onConfirm={confirmDeleteUnit}
      />

      <OrganizationExcelDialog
        open={excelOpen}
        onOpenChange={setExcelOpen}
        downloading={organizationStore.downloadingTemplate}
        importing={organizationStore.importing}
        requireCompany={true}
        companyOptions={modalCompanyOptions}
        onDownloadTemplate={({ companyId, level }) =>
          organizationStore.downloadImportTemplate({ companyId, level })
        }
        onImport={(file, { companyId }) =>
          organizationStore.importUnitsExcel(file, {
            adminAll: true,
            groupBy: resolveGroupBy(groupMode),
            companyId,
          })
        }
      />
    </div>
  );
});

export default AdminOrganizationPage;
